<template>
  <div>
    <section-title
      container-class="dispute-report-title"
      icon="fas fa-info-circle"
      title="1. IDEA Part B Dispute Resolution (Querellas) Data"
      title-class="font-14"
      reverse
      class="mt-4"
    />

    <div
      v-for="section in sections"
      :key="`section-${section.id}`"
      class="dispute-section mt-3"
    >
      <div class="dispute-section__header">
        <span class="dispute-section__badge">{{ section.code }}</span>
        <span>{{ section.label }}</span>
      </div>

      <div class="table-responsive dispute-section__table-wrapper">
        <table class="table dispute-section__table mb-0">
          <thead>
            <tr>
              <th class="description-column">Description</th>
              <th class="value-column text-center">
                <span>Prior Year</span>
                <small>{{ priorAcademicYear || '-' }}</small>
              </th>
              <th class="value-column text-center">
                <span>Current Year:</span>
                <small>{{ currentAcademicYear || '-' }}</small>
              </th>
              <th class="change-column text-center">%Change</th>
              <th class="error-column text-center">Error</th>
              <th class="edit-check-column">Edit Check</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in section.items" :key="`item-${item.itemId}`">
              <td>
                <div
                  class="dispute-description"
                  :class="{ 'dispute-description--root': isRootItem(item) }"
                  :style="{ paddingLeft: `${item.indentLevel * 24}px` }"
                >
                  <span>{{ item.code }}</span>
                  <span>{{ formatItemLabel(item.label) }}</span>
                  <span v-if="isRequiredItem(item)" class="required-indicator">*</span>
                  <span
                    v-if="item.dataNoteThresholdPercent"
                    class="pending-badge"
                  >
                    &gt;{{ formatThreshold(item.dataNoteThresholdPercent) }}%?
                  </span>
                </div>
              </td>
              <td class="text-center font-weight-bold">
                <input
                  v-if="canEditPriorYearValue(item)"
                  :value="item.priorYearValue"
                  class="form-control form-control-sm dispute-input"
                  :class="{ 'is-invalid': item.hasPriorYearRequiredError }"
                  max="9999999999"
                  min="0"
                  step="1"
                  type="number"
                  @input="onPriorYearValueInput(item, $event)"
                  @keydown="onNumericValueKeydown"
                  @paste="onPriorYearValuePaste(item, $event)"
                />
                <span v-else>{{ formatPriorYearValue(item.priorYearValue) }}</span>
              </td>
              <td class="text-center">
                <input
                  v-if="item.isEditable"
                  :value="item.currentYearValue"
                  :readonly="readonly"
                  class="form-control form-control-sm dispute-input"
                  :class="{ 'is-invalid': item.hasError || item.hasCurrentYearRequiredError }"
                  max="9999999999"
                  min="0"
                  step="1"
                  type="number"
                  @input="onCurrentYearValueInput(item, $event)"
                  @keydown="onNumericValueKeydown"
                  @paste="onCurrentYearValuePaste(item, $event)"
                />
                <span v-else>{{ formatValue(item.currentYearValue) }}</span>
              </td>
              <td class="text-center">
                <span v-if="getPercentChange(item) !== null" :class="getChangeClass(item)">
                  <i :class="getChangeIcon(item)" class="mr-1" />
                  {{ getPercentChange(item) }}%
                </span>
                <span v-else>-</span>
              </td>
              <td class="text-center">
                <i
                  v-if="item.hasError"
                  class="fas fa-exclamation-triangle text-danger"
                  :title="item.errorMessage"
                />
              </td>
              <td class="edit-check-column">
                {{ normalizeEditCheck(item.editCheckMessage) }}
              </td>
            </tr>
            <tr v-if="!section.items || !section.items.length">
              <td class="text-center text-muted" colspan="6">
                No hay datos para esta sección.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import SectionTitle from 'SectionTitle'

const MAX_NUMERIC_VALUE_DIGITS = 10

export default {
  name: 'ReportDetail',
  components: {
    SectionTitle,
  },
  props: {
    readonly: {
      type: Boolean,
      default: false,
    },
    sections: {
      type: Array,
      default: () => [],
    },
    priorAcademicYear: {
      type: String,
      default: null,
    },
    currentAcademicYear: {
      type: String,
      default: null,
    },
  },
  methods: {
    formatValue(value) {
      if (value === null || value === undefined || value === '') return '-'
      return Number(value).toLocaleString()
    },
    formatPriorYearValue(value) {
      if (value === null || value === undefined || value === '') return '-'
      return String(value).replace(/,/g, '')
    },
    formatItemLabel(value) {
      if (!value) return ''
      return String(value).replace(/\s*\*$/, '')
    },
    isRequiredItem(item) {
      return item.isEditable
    },
    formatThreshold(value) {
      return Number(value).toLocaleString(undefined, {
        maximumFractionDigits: 2,
      })
    },
    normalizeEditCheck(value) {
      if (!value || value === '-') return '-'
      return value
    },
    isRootItem(item) {
      return Number(item.indentLevel || 0) === 0
    },
    getPercentChange(item) {
      if (
        item.priorYearValue === null ||
        item.priorYearValue === undefined ||
        item.currentYearValue === null ||
        item.currentYearValue === undefined ||
        item.currentYearValue === ''
      ) {
        return null
      }

      const prior = Number(item.priorYearValue)
      const current = Number(item.currentYearValue)

      if (prior === current) {
        return null
      }

      if (prior === 0) {
        return '100.0'
      }

      return Math.abs(((current - prior) * 100) / prior).toFixed(1)
    },
    isDecrease(item) {
      return Number(item.currentYearValue) < Number(item.priorYearValue)
    },
    getChangeClass(item) {
      return this.isDecrease(item) ? 'text-success' : 'text-danger'
    },
    getChangeIcon(item) {
      return this.isDecrease(item) ? 'fas fa-caret-down' : 'fas fa-caret-up'
    },
    canEditPriorYearValue(item) {
      return !this.readonly && item.isEditable && item.isPriorYearEditable
    },
    validateRequiredFields() {
      let isValid = true

      ;(this.sections || []).forEach((section) => {
        ;(section.items || []).forEach((item) => {
          const isCurrentYearMissing =
            item.isEditable &&
            !this.readonly &&
            this.isEmptyValue(item.currentYearValue)
          const isPriorYearMissing =
            this.canEditPriorYearValue(item) &&
            this.isEmptyValue(item.priorYearValue)

          this.$set(item, 'hasCurrentYearRequiredError', isCurrentYearMissing)
          this.$set(item, 'hasPriorYearRequiredError', isPriorYearMissing)

          if (isCurrentYearMissing || isPriorYearMissing) {
            isValid = false
          }
        })
      })

      return isValid
    },
    hasPendingValidations() {
      return (this.sections || []).some((section) =>
        (section.items || []).some((item) =>
          item.hasError ||
          item.hasCurrentYearRequiredError ||
          item.hasPriorYearRequiredError
        )
      )
    },
    isEmptyValue(value) {
      return value === null || value === undefined || value === ''
    },
    onPriorYearValueInput(item, event) {
      this.onNumericValueInput(item, event, 'priorYearValue')
    },
    onCurrentYearValueInput(item, event) {
      this.onNumericValueInput(item, event, 'currentYearValue')
    },
    onPriorYearValuePaste(item, event) {
      this.onNumericValuePaste(item, event, 'priorYearValue')
    },
    onCurrentYearValuePaste(item, event) {
      this.onNumericValuePaste(item, event, 'currentYearValue')
    },
    onNumericValueInput(item, event, field) {
      const value = event.target.value

      this.clearRequiredValidation(item, field)

      if (value === '') {
        item[field] = null
        return
      }

      const integerValue = this.normalizeIntegerValue(value)
      const parsedValue = Number(integerValue)
      const normalizedValue = Number.isNaN(parsedValue) || parsedValue < 0 ? 0 : parsedValue

      item[field] = normalizedValue
      event.target.value = normalizedValue
    },
    clearRequiredValidation(item, field) {
      if (field === 'priorYearValue') {
        this.$set(item, 'hasPriorYearRequiredError', false)
      }

      if (field === 'currentYearValue') {
        this.$set(item, 'hasCurrentYearRequiredError', false)
      }
    },
    onNumericValueKeydown(event) {
      const invalidKeys = ['-', 'Subtract', '.', ',', 'Decimal', 'e', 'E', '+', 'Add']

      if (invalidKeys.includes(event.key)) {
        event.preventDefault()
      }
    },
    onNumericValuePaste(item, event, field) {
      const pastedValue = event.clipboardData?.getData('text')

      if (pastedValue === undefined || pastedValue === null) {
        return
      }

      event.preventDefault()
      this.clearRequiredValidation(item, field)

      const integerValue = this.normalizeIntegerValue(pastedValue)

      if (integerValue === '') {
        item[field] = null
        event.target.value = ''
        return
      }

      const normalizedValue = Number(integerValue)
      item[field] = normalizedValue
      event.target.value = normalizedValue
    },
    normalizeIntegerValue(value) {
      return String(value)
        .replace(',', '.')
        .split('.')[0]
        .replace(/\D/g, '')
        .slice(0, MAX_NUMERIC_VALUE_DIGITS)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variables/_colors.scss';

.dispute-section {
  border: 1px solid #d9e0e0;
  border-radius: 6px;
  overflow: hidden;
}

.dispute-section__header {
  align-items: center;
  background: #345f62;
  color: #fff;
  display: flex;
  font-size: 14px;
  font-weight: 700;
  gap: 10px;
  padding: 12px 16px;
}

.dispute-section__badge {
  align-items: center;
  background: #28c590;
  border-radius: 4px;
  display: inline-flex;
  height: 24px;
  justify-content: center;
  min-width: 24px;
}

.dispute-section__table {
  background: #fff;
  color: #203232;
  font-size: 13px;
}

.dispute-section__table th {
  background: #f7f7f7;
  border-top: 0;
  color: #203232;
  font-weight: 700;
  vertical-align: middle;
}

.dispute-section__table td {
  border-color: #e7ecec;
  vertical-align: middle;
}

.description-column {
  min-width: 330px;
  width: 44%;
}

.value-column {
  min-width: 120px;
  width: 12%;
}

.value-column span,
.value-column small {
  display: block;
}

.change-column {
  min-width: 100px;
  width: 10%;
}

.error-column {
  min-width: 80px;
  width: 8%;
}

.edit-check-column {
  min-width: 260px;
  width: 26%;
}

.dispute-description {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  font-weight: 400;
  gap: 4px;
}

.dispute-description--root {
  font-weight: 700;
}

.dispute-input {
  display: inline-block;
  max-width: 110px;
}

.dispute-input.is-invalid {
  background-image: none;
  border-color: #dc3545;
  padding-right: 0.5rem;
}

.pending-badge {
  background: #FFF1CC;
  border: 1px solid #D69A00;
  border-radius: 999px;
  color: #D69A00;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  margin-left: 6px;
  padding: 5px 9px;
}

::v-deep .dispute-report-title .dot {
  background-color: #fff;
}

::v-deep .dispute-report-title .title-icon {
  color: #ff6b57;
}
</style>
